import { Fragment, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { useAuthStore } from '../../stores/auth'
import { FilaCampaniiDiscount } from './FilaCampaniiDiscount'
import { ETICHETE_CIFRE } from '@b4l/shared'
import { DateQuickButtons, DateRangeField } from '../../components/ui/DateField'

type Canal = 'b2b' | 'emag' | 'shopify' | 'amazon'
const CANALE: Canal[] = ['b2b', 'emag', 'shopify', 'amazon']

const ETICHETA: Record<Canal, string> = {
  b2b: 'B2B (farmacii, distribuitori)',
  emag: 'eMAG',
  shopify: 'Shopify',
  amazon: 'Amazon',
}
const SCURT: Record<Canal, string> = {
  b2b: 'B2B',
  emag: 'eMAG',
  shopify: 'Shopify',
  amazon: 'Amazon',
}
const CULOARE: Record<Canal, string> = {
  b2b: '#2563eb',
  emag: '#f97316',
  shopify: '#16a34a',
  amazon: '#9333ea',
}

interface Totaluri {
  vanzari: number
  reduceri: number
  pretTaiat: number
  cost: number
  procent: number | null
  platforma: number
}

interface SumarResponse {
  interval: { de: string; pana: string }
  canale: (Totaluri & { canal: Canal })[]
  total: Totaluri
  luni: { luna: string; canale: Record<Canal, Totaluri>; total: Totaluri }[]
}

interface Produs extends Totaluri {
  canal: Canal
  sku: string
  denumire: string
  cantitate: number
}

interface ClientB2B {
  partener: string
  cif: string | null
  vanzari: number
  reduceri: { comerciala: number; campanie: number; publicitate: number }
  totalReduceri: number
  procent: number | null
}

interface FacturaReducere {
  data: string
  numar: string
  cod: string
  valoare: number
  /** Observația de pe factură (din XML-ul e-Factura), ex. „Achizitii Q1 2026”. */
  nota?: string | null
}

const TIP_COD: Record<string, string> = {
  RC1: 'Reducere comercială',
  redcamp: 'Reducere campanie',
  MK131: 'Publicitate la client',
  MK128: 'Publicitate la client',
}

const lei = (n: number) => n.toLocaleString('ro-RO', { maximumFractionDigits: 0 })
const pct = (p: number | null) =>
  p == null
    ? '—'
    : `${p.toLocaleString('ro-RO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
const zi = (s: string) => `${s.slice(8, 10)}.${s.slice(5, 7)}.${s.slice(0, 4)}`
const lunaScurta = (l: string) => `${l.slice(5, 7)}.${l.slice(2, 4)}`

function aziYmd(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Bucharest' }).format(new Date())
}

const LUNI = [
  'Ianuarie',
  'Februarie',
  'Martie',
  'Aprilie',
  'Mai',
  'Iunie',
  'Iulie',
  'August',
  'Septembrie',
  'Octombrie',
  'Noiembrie',
  'Decembrie',
]

/** „Toată perioada” din filtru = de aici; serverul taie lunile goale de la început. */
const INCEPUT_DATE = '2020-01-01'

const ultimaZi = (luna: string) =>
  `${luna}-${String(new Date(Number(luna.slice(0, 4)), Number(luna.slice(5, 7)), 0).getDate()).padStart(2, '0')}`

/** Lunile pentru selecția rapidă: de la ianuarie anul trecut până luna curentă, cele noi primele. */
function luniDeAles(azi: string): { luna: string; eticheta: string }[] {
  const out: { luna: string; eticheta: string }[] = []
  let a = Number(azi.slice(0, 4))
  let l = Number(azi.slice(5, 7))
  const aStart = a - 1
  while (a > aStart || (a === aStart && l >= 1)) {
    out.push({ luna: `${a}-${String(l).padStart(2, '0')}`, eticheta: `${LUNI[l - 1]} ${a}` })
    l -= 1
    if (l < 1) {
      l = 12
      a -= 1
    }
  }
  return out
}

/**
 * Vânzări → Discounturi acordate: cât ne costă promoțiile pe fiecare canal, lunar, în lei fără TVA.
 * Design: docs/superpowers/specs/2026-11-01-discounturi-acordate-design.md
 */
export function DiscounturiAcordatePage() {
  const azi = aziYmd()
  const [de, setDe] = useState(`${azi.slice(0, 4)}-01-01`)
  const [pana, setPana] = useState(azi)
  const [fila, setFila] = useState<'produse' | 'clienti' | 'campanii'>('produse')
  const poateIncadra = useAuthStore((s) => s.permissions).includes('discounturi:write')
  const [canal, setCanal] = useState<'' | Canal>('')
  const vizibile = canal ? [canal] : CANALE
  // Filtrul pe canal alege si fila: B2B are detaliul pe client si campaniile, celelalte doar pe produs.
  const filaActiva =
    canal === 'b2b' ? (fila === 'campanii' ? 'campanii' : 'clienti') : canal ? 'produse' : fila
  const [exportIn, setExportIn] = useState(false)

  // „Toată perioada” lasă ambele capete goale.
  const params = new URLSearchParams({ de: de || INCEPUT_DATE, pana: pana || azi }).toString()
  const luni = luniDeAles(azi)
  const lunaAleasa =
    luni.find(
      (l) =>
        de === `${l.luna}-01` &&
        (pana === ultimaZi(l.luna) || (pana === azi && azi.startsWith(l.luna))),
    )?.luna ?? ''

  const { data, isLoading, isError } = useQuery<SumarResponse>({
    queryKey: ['discounturi', de, pana],
    queryFn: async () => (await api.get(`/discounturi?${params}`, { timeout: 120_000 })).data,
  })

  async function exporta() {
    setExportIn(true)
    try {
      const res = await api.get(`/discounturi/export?${params}${canal ? `&canal=${canal}` : ''}`, {
        responseType: 'blob',
        timeout: 180_000,
      })
      const href = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = href
      a.download = `discounturi-${canal ? `${canal}-` : ''}${de || INCEPUT_DATE}-${pana || azi}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(href)
    } catch {
      toast.error('Exportul n-a reușit')
    } finally {
      setExportIn(false)
    }
  }

  const grafic = useMemo(
    () =>
      (data?.luni ?? []).map((l) => ({
        luna: lunaScurta(l.luna),
        ...Object.fromEntries(CANALE.map((c) => [c, Math.round(l.canale[c].cost)])),
      })),
    [data],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Discounturi acordate</h1>
          <p className="text-sm text-slate-500">
            Cât ne costă promoțiile pe fiecare canal: reducerile la vânzare și prețul tăiat, în lei
            fără TVA, ca valoare și ca procent din vânzările de dinainte de discount.
          </p>
        </div>
        <button
          className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          disabled={exportIn}
          onClick={exporta}
        >
          {exportIn ? 'Se pregătește…' : 'Export XLSX'}
        </button>
      </div>

      <Card title="Filtre">
        <DateQuickButtons
          fromValue={de}
          toValue={pana}
          onChange={(a, b) => {
            setDe(a)
            setPana(b)
          }}
        />
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <DateRangeField
              fromValue={de}
              toValue={pana}
              onChange={(a, b) => {
                setDe(a)
                setPana(b)
              }}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
              O singură lună
            </label>
            <select
              className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
              value={lunaAleasa}
              onChange={(e) => {
                const l = e.target.value
                if (!l) return
                setDe(`${l}-01`)
                setPana(ultimaZi(l))
              }}
            >
              <option value="">Alege luna…</option>
              {luni.map((l) => (
                <option key={l.luna} value={l.luna}>
                  {l.eticheta}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Canal</label>
            <select
              className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
              value={canal}
              onChange={(e) => setCanal(e.target.value as '' | Canal)}
            >
              <option value="">Toate canalele</option>
              {CANALE.map((c) => (
                <option key={c} value={c}>
                  {SCURT[c]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2">Canal</th>
              <th
                className="px-3 py-2 text-right"
                title={`Fără TVA. La B2B: ${ETICHETE_CIFRE.vanzariB2BInainteDeReduceri.eticheta.toLowerCase()}.`}
              >
                Vânzări înainte de discount
              </th>
              <th className="px-3 py-2 text-right">Reduceri la vânzare</th>
              <th className="px-3 py-2 text-right">Preț tăiat</th>
              <th className="px-3 py-2 text-right">Cost promoții (noi)</th>
              <th className="px-3 py-2 text-right">% din vânzări</th>
              <th
                className="px-3 py-2 text-right"
                title="Voucherele eMAG plătite de eMAG — nu intră în cost"
              >
                Suportat de platformă
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                  Se calculează…
                </td>
              </tr>
            )}
            {isError && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-red-600">
                  Nu s-au putut încărca datele.
                </td>
              </tr>
            )}
            {data?.canale
              .filter((c) => vizibile.includes(c.canal))
              .map((c) => (
                <tr key={c.canal} className="border-b">
                  <td className="px-3 py-2">
                    <span
                      className="mr-2 inline-block h-2.5 w-2.5 rounded-full"
                      style={{ background: CULOARE[c.canal] }}
                    />
                    {ETICHETA[c.canal]}
                  </td>
                  <td className="px-3 py-2 text-right">{lei(c.vanzari)}</td>
                  <td className="px-3 py-2 text-right">{lei(c.reduceri)}</td>
                  <td className="px-3 py-2 text-right">{c.pretTaiat ? lei(c.pretTaiat) : '—'}</td>
                  <td className="px-3 py-2 text-right font-semibold">{lei(c.cost)}</td>
                  <td className="px-3 py-2 text-right">{pct(c.procent)}</td>
                  <td className="px-3 py-2 text-right text-slate-500">
                    {c.platforma ? lei(c.platforma) : '—'}
                  </td>
                </tr>
              ))}
            {data && !canal && (
              <tr className="bg-slate-50 font-semibold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{lei(data.total.vanzari)}</td>
                <td className="px-3 py-2 text-right">{lei(data.total.reduceri)}</td>
                <td className="px-3 py-2 text-right">{lei(data.total.pretTaiat)}</td>
                <td className="px-3 py-2 text-right">{lei(data.total.cost)}</td>
                <td className="px-3 py-2 text-right">{pct(data.total.procent)}</td>
                <td className="px-3 py-2 text-right text-slate-500">{lei(data.total.platforma)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      <ul className="list-disc space-y-1 pl-5 text-xs text-slate-500">
        {vizibile.includes('b2b') && (
          <li>
            <b>B2B:</b> vânzările sunt liniile de produs înainte de reduceri, fără TVA; reducerile
            sunt facturile separate de discount din EA (reducere comercială, reducere campanie,
            publicitate la client) și apar în luna facturii de reducere, nu în luna vânzărilor pe
            care le recompensează — o reducere trimestrială apare toată într-o lună.
          </li>
        )}
        {vizibile.includes('emag') && (
          <li>
            <b>eMAG:</b> în cost intră doar voucherele suportate de noi; cele plătite de eMAG sunt
            la „Suportat de platformă”. Doar comenzile finalizate.
          </li>
        )}
        {vizibile.includes('shopify') && (
          <li>
            <b>Shopify:</b> prețul tăiat se calculează față de prețul de listă din EA de acum, cu
            TVA. Pachetele promo fără cod de produs nu au preț tăiat.
          </li>
        )}
        {vizibile.includes('amazon') && (
          <li>
            <b>Amazon:</b> rabaturile din promoții, convertite din EUR la cursul facturii; fără preț
            tăiat (Amazon nu dă preț de listă).
          </li>
        )}
      </ul>

      {data && (
        <Card className="p-4">
          <div className="mb-3 text-sm font-medium text-slate-700">
            Cost promoții (noi) pe lună, lei fără TVA
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={grafic}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="luna" style={{ fontSize: 11 }} />
              <YAxis style={{ fontSize: 11 }} tickFormatter={(v) => lei(Number(v))} width={70} />
              <Tooltip formatter={(v, name) => [`${lei(Number(v))} lei`, SCURT[name as Canal]]} />
              <Legend formatter={(v) => SCURT[v as Canal]} />
              {vizibile.map((c) => (
                <Bar key={c} dataKey={c} fill={CULOARE[c]} radius={[3, 3, 0, 0]} maxBarSize={28} />
              ))}
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b text-left text-slate-500">
                <tr>
                  <th className="px-2 py-1">Luna</th>
                  {vizibile.map((c) => (
                    <th key={c} className="px-2 py-1 text-right">
                      {SCURT[c]}
                    </th>
                  ))}
                  {!canal && <th className="px-2 py-1 text-right">Total</th>}
                </tr>
              </thead>
              <tbody>
                {data.luni.map((l) => (
                  <tr key={l.luna} className="border-b last:border-0">
                    <td className="px-2 py-1">{lunaScurta(l.luna)}</td>
                    {vizibile.map((c) => (
                      <td key={c} className="px-2 py-1 text-right">
                        {l.canale[c].cost ? lei(l.canale[c].cost) : '—'}
                        <span className="ml-1 text-slate-400">{pct(l.canale[c].procent)}</span>
                      </td>
                    ))}
                    {!canal && (
                      <td className="px-2 py-1 text-right font-medium">
                        {lei(l.total.cost)}
                        <span className="ml-1 font-normal text-slate-400">
                          {pct(l.total.procent)}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="flex gap-1 border-b text-sm">
        {(
          [
            ['produse', 'Pe produs (eMAG, Shopify, Amazon)'],
            ['clienti', 'Pe client B2B'],
            ['campanii', 'Campanii B2B'],
          ] as const
        )
          .filter(([k]) =>
            !canal
              ? true
              : canal === 'b2b'
                ? k === 'clienti' || k === 'campanii'
                : k === filaActiva,
          )
          .map(([k, label]) => (
            <button
              key={k}
              className={`-mb-px border-b-2 px-4 py-2 ${
                filaActiva === k
                  ? 'border-slate-900 font-medium'
                  : 'border-transparent text-slate-500'
              }`}
              onClick={() => setFila(k)}
            >
              {label}
            </button>
          ))}
      </div>

      {filaActiva === 'produse' && (
        <PeProdus params={params} de={de} pana={pana} canalFix={canal || undefined} />
      )}
      {filaActiva === 'clienti' && <PeClient params={params} de={de} pana={pana} />}
      {filaActiva === 'campanii' && (
        <FilaCampaniiDiscount params={params} de={de} pana={pana} poateScrie={poateIncadra} />
      )}
    </div>
  )
}

function PeProdus({
  params,
  de,
  pana,
  canalFix,
}: {
  params: string
  de: string
  pana: string
  /** Canalul ales în filtrul de sus: înlocuiește butoanele de canal din filă. */
  canalFix?: Canal
}) {
  const [canalFila, setCanal] = useState<'' | Canal>('')
  const canal = canalFix ?? canalFila
  const [cauta, setCauta] = useState('')
  const [toate, setToate] = useState(false)

  const { data, isLoading } = useQuery<{ items: Produs[] }>({
    queryKey: ['discounturi', 'produse', de, pana, canal],
    queryFn: async () =>
      (
        await api.get(`/discounturi/produse?${params}${canal ? `&canal=${canal}` : ''}`, {
          timeout: 120_000,
        })
      ).data,
  })

  const filtrate = useMemo(() => {
    const t = cauta.trim().toLowerCase()
    const items = data?.items ?? []
    return t
      ? items.filter((p) => p.sku.toLowerCase().includes(t) || p.denumire.toLowerCase().includes(t))
      : items
  }, [data, cauta])
  const afisate = toate ? filtrate : filtrate.slice(0, 100)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {!canalFix &&
          (['', 'emag', 'shopify', 'amazon'] as const).map((c) => (
            <button
              key={c || 'toate'}
              className={`rounded-full px-3 py-1 ${
                canal === c ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'
              }`}
              onClick={() => setCanal(c)}
            >
              {c === '' ? 'Toate' : SCURT[c]}
            </button>
          ))}
        <input
          className="ml-auto rounded-md border px-2 py-1"
          placeholder="Caută cod sau produs"
          value={cauta}
          onChange={(e) => setCauta(e.target.value)}
        />
      </div>
      <Card className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-left text-slate-500">
            <tr>
              <th className="px-3 py-2">Canal</th>
              <th className="px-3 py-2">Cod</th>
              <th className="px-3 py-2">Produs</th>
              <th className="px-3 py-2 text-right">Cantitate</th>
              <th className="px-3 py-2 text-right">Vânzări înainte de discount</th>
              <th className="px-3 py-2 text-right">Reduceri</th>
              <th className="px-3 py-2 text-right">Preț tăiat</th>
              <th className="px-3 py-2 text-right">Cost</th>
              <th className="px-3 py-2 text-right">%</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-slate-400">
                  Se calculează…
                </td>
              </tr>
            )}
            {!isLoading && filtrate.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-slate-400">
                  Niciun produs în interval.
                </td>
              </tr>
            )}
            {afisate.map((p) => (
              <tr key={`${p.canal}-${p.sku}`} className="border-b last:border-0">
                <td className="px-3 py-2 whitespace-nowrap">{SCURT[p.canal]}</td>
                <td className="px-3 py-2 font-mono text-xs">{p.sku}</td>
                <td className="px-3 py-2">{p.denumire}</td>
                <td className="px-3 py-2 text-right">{lei(p.cantitate)}</td>
                <td className="px-3 py-2 text-right">{lei(p.vanzari)}</td>
                <td className="px-3 py-2 text-right">{p.reduceri ? lei(p.reduceri) : '—'}</td>
                <td className="px-3 py-2 text-right">{p.pretTaiat ? lei(p.pretTaiat) : '—'}</td>
                <td className="px-3 py-2 text-right font-medium">{lei(p.cost)}</td>
                <td className="px-3 py-2 text-right">{pct(p.procent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      {!toate && filtrate.length > 100 && (
        <button className="text-sm text-slate-600 underline" onClick={() => setToate(true)}>
          Arată toate cele {filtrate.length} de produse
        </button>
      )}
    </div>
  )
}

function PeClient({ params, de, pana }: { params: string; de: string; pana: string }) {
  const [deschis, setDeschis] = useState<ClientB2B | null>(null)

  const { data, isLoading } = useQuery<{ items: ClientB2B[] }>({
    queryKey: ['discounturi', 'clienti', de, pana],
    queryFn: async () =>
      (await api.get(`/discounturi/clienti-b2b?${params}`, { timeout: 120_000 })).data,
  })

  const facturi = useQuery<{ items: FacturaReducere[] }>({
    queryKey: ['discounturi', 'facturi', de, pana, deschis?.partener, deschis?.cif],
    enabled: deschis != null,
    queryFn: async () => {
      const q = new URLSearchParams(params)
      q.set('partener', deschis!.partener)
      if (deschis!.cif) q.set('cif', deschis!.cif)
      return (await api.get(`/discounturi/clienti-b2b/facturi?${q}`)).data
    },
  })

  const items = data?.items ?? []
  const cheie = (c: ClientB2B) => `${c.cif ?? ''}|${c.partener}`

  return (
    <Card className="overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="border-b bg-slate-50 text-left text-slate-500">
          <tr>
            <th className="px-3 py-2">Client</th>
            <th className="px-3 py-2 text-right">
              {ETICHETE_CIFRE.vanzariB2BInainteDeReduceri.eticheta} ·{' '}
              {ETICHETE_CIFRE.vanzariB2BInainteDeReduceri.detaliu}
            </th>
            <th className="px-3 py-2 text-right">Reducere comercială</th>
            <th className="px-3 py-2 text-right">Reducere campanie</th>
            <th className="px-3 py-2 text-right">Publicitate la client</th>
            <th className="px-3 py-2 text-right">Total reduceri</th>
            <th className="px-3 py-2 text-right">%</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                Se calculează…
              </td>
            </tr>
          )}
          {!isLoading && items.length === 0 && (
            <tr>
              <td colSpan={7} className="px-3 py-6 text-center text-slate-400">
                Nicio factură de reducere în interval.
              </td>
            </tr>
          )}
          {items.map((c) => {
            const eDeschis = deschis != null && cheie(deschis) === cheie(c)
            return (
              <Fragment key={cheie(c)}>
                <tr
                  className="cursor-pointer border-b last:border-0 hover:bg-slate-50"
                  onClick={() => setDeschis(eDeschis ? null : c)}
                >
                  <td className="px-3 py-2 font-medium">
                    {eDeschis ? '▾' : '▸'} {c.partener}
                    {c.cif && <span className="ml-2 text-xs text-slate-400">{c.cif}</span>}
                  </td>
                  <td className="px-3 py-2 text-right">{lei(c.vanzari)}</td>
                  <td className="px-3 py-2 text-right">
                    {c.reduceri.comerciala ? lei(c.reduceri.comerciala) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {c.reduceri.campanie ? lei(c.reduceri.campanie) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {c.reduceri.publicitate ? lei(c.reduceri.publicitate) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">{lei(c.totalReduceri)}</td>
                  <td className="px-3 py-2 text-right">{pct(c.procent)}</td>
                </tr>
                {eDeschis && (
                  <tr className="border-b bg-slate-50/50">
                    <td colSpan={7} className="px-3 py-3">
                      {facturi.isLoading && <div className="text-slate-400">Se încarcă…</div>}
                      {facturi.data && (
                        <table className="w-full text-xs">
                          <thead className="text-left text-slate-500">
                            <tr>
                              <th className="px-2 py-1">Data</th>
                              <th className="px-2 py-1">Factura</th>
                              <th className="px-2 py-1">Tip</th>
                              <th className="px-2 py-1">Detaliu pe factură</th>
                              <th className="px-2 py-1 text-right">Reducere (lei fără TVA)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {facturi.data.items.map((f) => (
                              <tr key={`${f.numar}-${f.cod}`} className="border-t border-slate-200">
                                <td className="px-2 py-1">{zi(f.data)}</td>
                                <td className="px-2 py-1">{f.numar}</td>
                                <td className="px-2 py-1">{TIP_COD[f.cod] ?? f.cod}</td>
                                <td className="px-2 py-1 text-slate-600">{f.nota ?? '—'}</td>
                                <td className="px-2 py-1 text-right">{lei(f.valoare)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            )
          })}
        </tbody>
      </table>
    </Card>
  )
}
